import { useCallback, useEffect, useState } from 'react'
import { FirebaseApp } from 'firebase/app'
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore/lite'
import styled from 'styled-components'
import { FirebaseSessionKey, isMobile } from '../constants'
import { Table, Tag, Button, Drawer, Spin, Card, PageHeader } from 'antd'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EyeOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import { isEmpty } from 'lodash'

type Props = {
  firebaseApp: FirebaseApp
  setActiveMenu: (value?: string) => void
}

type Question = {
  _id: string
  question: string
  a1: string
  a2: string
  a3: string
  answer: string
  image: string
  userAnswer: string
}

const ICNFResults = ({ firebaseApp, setActiveMenu }: Props) => {
  const [results, setResults] = useState<Record<string, any>[] | undefined>(
    undefined
  )

  const [reviewOpen, setReviewOpen] = useState(false)
  const [activeReview, setActiveReview] = useState<Question[] | undefined>(
    undefined
  )

  const resultsCall = useCallback(async () => {
    const user = JSON.parse(
      window.sessionStorage.getItem(FirebaseSessionKey) as string
    )
    const db = getFirestore(firebaseApp)
    const citiesCol = collection(db, 'pastexams')
    const q = query(
      citiesCol,
      where('user_id', '==', user.uid),
      where('exam_id', '==', 'icnf')
    )
    const querySnapshot = await getDocs(q)
    const eventList = querySnapshot.docs.map((doc) => ({
      ...doc.data(),
      key: doc.id,
    }))
    return eventList
  }, [firebaseApp])

  const makeResultsGet = useCallback(() => {
    resultsCall().then((data: any) => {
      const sortedData = data.sort(
        (a: any, b: any) => b.date_finished.toDate() - a.date_finished.toDate()
      )
      setResults(sortedData)
    })
  }, [resultsCall])

  useEffect(() => {
    if (!results) {
      setTimeout(() => makeResultsGet(), 2000)
    }
  }, [makeResultsGet, results])

  const columns: any = [
    {
      title: 'Data',
      dataIndex: 'date_finished',
      key: 'date_finished',
      align: isMobile ? 'center' : 'left',
      render: (date_finished: any) => (
        <span>
          {moment(date_finished.toDate()).format(
            isMobile ? 'DD/MM/YYYY' : 'DD/MM/YYYY - HH:mm:ss'
          )}
        </span>
      ),
    },
    {
      title: 'Resultado',
      dataIndex: 'exam_status',
      key: 'exam_status',
      align: isMobile ? 'center' : 'left',
      render: (exam_status: boolean) => (
        <Tag color={exam_status ? 'green' : 'volcano'} key={'exam_status'}>
          {exam_status ? 'Aprovado' : 'Reprovado'}
        </Tag>
      ),
    },
    {
      title: 'Nota',
      dataIndex: 'exam_percentage',
      key: 'exam_percentage',
      render: (exam_percentage: number) => (
        <Tag
          color={exam_percentage >= 75 ? 'green' : 'volcano'}
          key={'wrong_answers'}
        >
          {exam_percentage}%
        </Tag>
      ),
      responsive: ['md'],
    },
    {
      title: 'Perguntas Erradas',
      key: 'wrong_answers',
      dataIndex: 'wrong_answers',
      responsive: ['md'],
      render: (wrong_answers: string[]) => (
        <Tag
          color={wrong_answers.length === 0 ? 'green' : 'volcano'}
          key={'wrong_answers'}
        >
          {wrong_answers.length}
        </Tag>
      ),
    },
    {
      title: 'Rever',
      key: 'action',
      align: isMobile ? 'center' : 'left',
      render: (_: string, record: Record<string, any>) => {
        if (record.wrong_answers.length === 0) {
          return null
        } else {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setReviewOpen(true)
                setActiveReview(record.wrong_answers)
              }}
            >
              {isMobile ? <EyeOutlined /> : 'Rever Perguntas Erradas'}
            </Button>
          )
        }
      },
    },
  ]

  const modalContent = () => {
    if (!activeReview) {
      return (
        <Loading>
          <Spin tip="A Carregar..." size="large" />
        </Loading>
      )
    } else {
      const renderImage = (question: Question) => {
        if (isEmpty(question.image)) {
          return null
        } else {
          return (
            <QuestionImageWrapper>
              <img src={question.image} alt="" />
            </QuestionImageWrapper>
          )
        }
      }

      return (activeReview || []).map((q: Question) => (
        <Card
          type="inner"
          title={
            <p
              style={{
                fontWeight: 'bold',
                margin: '0',
                fontSize: '16px',
                whiteSpace: 'normal',
              }}
            >
              {q.question}
            </p>
          }
          style={{ marginBottom: '15px', fontWeight: 'bold' }}
        >
          {renderImage(q)}
          <StyledWrongAnswer>
            <CloseCircleFilled
              style={{ fontSize: '20px', marginRight: '5px' }}
            />
            {q[q.userAnswer as keyof Question]}
          </StyledWrongAnswer>
          <StyledRightAnswer>
            <CheckCircleFilled
              style={{ fontSize: '20px', marginRight: '5px' }}
            />
            {q[q.answer as keyof Question]}
          </StyledRightAnswer>
        </Card>
      ))
    }
  }

  return (
    <Container>
      <Drawer
        width={isMobile ? '100%' : 800}
        title="Rever Perguntas Erradas"
        placement="right"
        onClose={() => setReviewOpen(false)}
        visible={reviewOpen}
      >
        {modalContent()}
      </Drawer>
      <StyledPageHeader
        onBack={() => setActiveMenu(undefined)}
        title={<StyledPageText>Voltar</StyledPageText>}
      />
      <Table
        scroll={isMobile ? { x: 'scroll', y: 'scroll' } : undefined}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        columns={columns}
        dataSource={results}
        loading={!results}
      />
    </Container>
  )
}

export default ICNFResults

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 100px);
  max-height: calc(100% - 100px);
  background-color: #ececec;
  padding: 10px;

  @media (max-width: 768px) {
    overflow: auto;
  }
`

const StyledPageText = styled.span`
  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const StyledPageHeader = styled(PageHeader)`
  padding: 0;
`

const Loading = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`
const StyledWrongAnswer = styled.p`
  color: red;
  display: flex;
  align-items: center;
`
const StyledRightAnswer = styled.p`
  color: green;
  display: flex;
  align-items: center;
`
const QuestionImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`
